/**
 * 本地草稿暂存（zustand + sessionStorage）
 * -----------------------------------------------------------------------------
 * 定位：**「填一个字段存一个」的零延迟兜底**。服务端草稿（`PATCH /registrations/{id}`）
 * 才是权威来源，但它要跨网络、要节流，弱网或进程被系统回收时必然有丢失窗口；
 * 这里每次变更同步写一次，把窗口压到 0。
 *
 * 为什么是 `sessionStorage` 而不是 `localStorage`：草稿里是姓名、手机号、身份证号、
 * 收件地址这类个人信息，会话级存储随标签页关闭而清除，暴露面更小
 * （`07-留存与合规.md` 的最小化取向）。代价是关掉页面再打开就没有本地兜底 ——
 * 那份由服务端草稿接手，两边互补。
 *
 * ⚠️ **收件地址是例外，单独走 `localStorage`**（2026-10-26 真机事故）：
 * 地址要等「收件人姓名 + 手机号」都齐了才能发服务端（服务端六字段全必填），
 * 而手机号是第 3 步才验证的 —— 学员在第 1 步填完地址、还没验手机号就退出，
 * 地址只存在于本地；微信里从菜单重进是**新的 webview**，sessionStorage 直接没了，
 * 于是「重新进入填写时会丢失」。地址那一份存的本就只有省市区码 + 门牌号
 * （姓名 / 手机号是发送前才补的，从不落本地），敏感度低于表单草稿，
 * 因此允许它跨会话保留 **30 天**。
 *
 * ⚠️ 读取时**本地优先**（`{...server, ...local}`）：本地那份可能比服务端更新
 * （自动保存还没发出去）。但保存成功后必须 `clear`，否则本地会永远盖住服务端。
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ShippingAddressInput } from '../api/types';

export type Answers = Record<string, unknown>;

interface DraftState {
  /** 报名单 id → 未成功同步到服务端的表单值 */
  drafts: Record<string, Answers>;
  /** 报名单 id → 本地暂存时间戳 */
  savedAt: Record<string, number>;
  save: (registrationId: string, answers: Answers) => void;
  /**
   * 只清表单（表单已成功存到服务端）
   *
   * 与 `clearAddress` 分开是必须的：两者是**两条独立的保存链路**，
   * 表单存成功就把地址的本地兜底一起清掉，等于让还没存上的地址凭空消失。
   */
  clearAnswers: (registrationId: string) => void;
  /** 清掉这张单的全部本地暂存（提交成功后调用） */
  clear: (registrationId: string) => void;
}

export const useDraftStore = create<DraftState>()(
  persist(
    (set) => ({
      drafts: {},
      savedAt: {},
      save: (registrationId, answers) =>
        set((state) => ({
          drafts: { ...state.drafts, [registrationId]: answers },
          savedAt: { ...state.savedAt, [registrationId]: Date.now() },
        })),
      clearAnswers: (registrationId) =>
        set((state) => {
          const drafts = { ...state.drafts };
          delete drafts[registrationId];
          return { drafts };
        }),
      clear: (registrationId) => {
        clearLocalAddress(registrationId);
        set((state) => {
          const drafts = { ...state.drafts };
          const savedAt = { ...state.savedAt };
          delete drafts[registrationId];
          delete savedAt[registrationId];
          return { drafts, savedAt };
        });
      },
    }),
    {
      name: 'enroll.draft',
      storage: createJSONStorage(() => window.sessionStorage),
      partialize: (state) => ({
        drafts: state.drafts,
        savedAt: state.savedAt,
      }),
    },
  ),
);

/** 本地暂存的表单兜底值（无则 null） */
export function readLocalDraft(registrationId: string): Answers | null {
  return useDraftStore.getState().drafts[registrationId] ?? null;
}

// ---------------------------------------------------------------- 收件地址

/**
 * 地址本地兜底单独一个键：与表单草稿**分属两种存储**（地址走 `localStorage`，
 * 表单走 `sessionStorage`），见文件头说明。
 */
const ADDRESS_STORAGE_KEY = 'enroll.addr.v1';

/** 地址在本机留多久。地址只含省市区码与门牌号，30 天足够覆盖「填一半去翻身份证」的间隔 */
const ADDRESS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredAddress {
  value: ShippingAddressInput;
  /** 写入时间（毫秒）：只用于过期判定 */
  at: number;
}

type AddressBucket = Record<string, StoredAddress>;

/**
 * 读整桶。
 *
 * 任何异常都按「没有」处理：存储被禁用（隐私模式 / 配额满）时不该让报名页崩掉 ——
 * 兜底是加分项，不是主链路。
 */
function readBucket(): AddressBucket {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(ADDRESS_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as AddressBucket;
  } catch (error) {
    // 存储里是脏数据（手工改过 / 旧版本格式）→ 丢弃，不影响主流程
    window.localStorage.removeItem(ADDRESS_STORAGE_KEY);
    console.warn('本地地址暂存读取失败，已重置', error);
    return {};
  }
}

function writeBucket(bucket: AddressBucket): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ADDRESS_STORAGE_KEY, JSON.stringify(bucket));
  } catch (error) {
    // 配额满 / 隐私模式：静默降级为「本次会话有效」，不能让保存动作报错
    console.warn('本地地址暂存写入失败', error);
  }
}

/** 本地暂存的地址兜底值（无 / 已过期则 null） */
export function readLocalAddress(registrationId: string): ShippingAddressInput | null {
  const bucket = readBucket();
  const entry = bucket[registrationId];
  if (!entry) return null;
  if (Date.now() - entry.at > ADDRESS_TTL_MS) {
    delete bucket[registrationId];
    writeBucket(bucket);
    return null;
  }
  return entry.value;
}

/** 写本地地址兜底（半填状态也留 —— 服务端存不了半份） */
export function saveLocalAddress(registrationId: string, address: ShippingAddressInput): void {
  const bucket = readBucket();
  bucket[registrationId] = { value: address, at: Date.now() };
  writeBucket(bucket);
}

/** 清本地地址兜底（地址已成功存到服务端，或整张单已提交） */
export function clearLocalAddress(registrationId: string): void {
  const bucket = readBucket();
  if (!(registrationId in bucket)) return;
  delete bucket[registrationId];
  writeBucket(bucket);
}
